<template>
  <v-dialog
    v-model="isDialogVisible"
    width="50%"
    @click:outside="onHandleDialogCancel"
    @keydown.esc="onHandleDialogCancel"
  >
    <v-card shadow>
      <v-toolbar
        color="blue-grey-darken-2"
      >
        <v-toolbar-title>
          {{ $t('IdentityUserDialogs.headline_update') }}
        </v-toolbar-title>
      </v-toolbar>

      <v-form v-model="isFormValid" class="ma-5" ref="form">
          <v-row class="ml-5 mr-5">
            <v-text-field
              clearable
              v-model="updateRequest.email"
              :rules="[
                (value: any) => !updateRequest.email
                || HelperFunctions.IsEmailValid(updateRequest.email)
                || $t('IdentityUserDialogs.rule_email_invalid')
              ]"
              :label="$t('IdentityUserDialogs.email')"
            ></v-text-field>
          </v-row>
          <v-row class="ml-5 mr-5">
            <v-text-field
              clearable
              v-model="updateRequest.firstName"
              :counter="MAX_LENGTH_FIRST_NAME"
              :rules="[(value: any) => !updateRequest.firstName
              || isSmallerOrEqualToMaxLength(updateRequest.firstName, MAX_LENGTH_FIRST_NAME)
              || $t('IdentityUserDialogs.rule_max_length', {maxLength: MAX_LENGTH_FIRST_NAME})]"
              :label="$t('IdentityUserDialogs.first_name')"
            ></v-text-field>
          </v-row>
          <v-row class="ml-5 mr-5">
            <v-text-field
              clearable
              v-model="updateRequest.lastName"
              :counter="MAX_LENGTH_LAST_NAME"
              :rules="[(value: any) => !updateRequest.lastName
              || isSmallerOrEqualToMaxLength(updateRequest.lastName, MAX_LENGTH_LAST_NAME)
              || $t('IdentityUserDialogs.rule_max_length', {maxLength: MAX_LENGTH_LAST_NAME})]"
              :label="$t('IdentityUserDialogs.last_name')"
            ></v-text-field>
          </v-row>
          <v-row class="ml-5 mr-5">
            <v-col>
              <v-checkbox
                v-model="updateRequest.isEmailVerified"
                :label="$t('IdentityUserDialogs.verified')"
                color="green"
                hide-details
              ></v-checkbox>
            </v-col>
          </v-row>
      </v-form>

      <v-card-actions>
        <v-row
          align="center"
          justify="center"
          no-gutters
        >
          <v-btn
            color="primary"
            variant="text"
            @click="onHandleDialogCancel"
          >
            {{ $t('IdentityUserDialogs.button_cancel') }}
          </v-btn>

          <v-btn
            color="primary"
            variant="text"
            @click="onHandleDialogConfirm"
            :disabled="!isFormValid"
          >
            {{ $t('IdentityUserDialogs.button_confirm') }}
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import {
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";

import {
  updateIdentityUser,
} from "@/components/children/identityUsers/scripts";
import type {
  UpdateIdentityUserRequest,
} from "@/components/children/identityUsers/scripts";

import type {
  User,
} from "@/scripts/Common";

import {
  isSmallerOrEqualToMaxLength,
} from "@/scripts/Common/validators";

import {
  HelperFunctions,
  isEmpty,
} from "@/scripts/Common/HelperFunctions";

import {
  MAX_LENGTH_FIRST_NAME,
  MAX_LENGTH_LAST_NAME,
} from "@/components/children/identityUsers/constants";

import {
  VForm,
} from "vuetify/components";
import type { PropType } from 'vue';

const emits = defineEmits([
  'onDialogCancel',
  'onDialogConfirm',
  'onFetchError'
]);

const props = defineProps({
  user: {
    required: true,
    type: Object as PropType<User>
  }
});

const form = ref<InstanceType<typeof VForm>>();
const updateRequest = ref<UpdateIdentityUserRequest>({
  email: props.user?.email,
  firstName: props.user?.firstName,
  lastName: props.user?.lastName,
  isEmailVerified: props.user?.isEmailVerified,
})

const isFormValid = ref<any>(false);

watch(isFormValid, (val) => {
  if (typeof val === 'boolean') return;
  isFormValid.value = !!val?.valid;
});

const isDialogVisible = ref(false);

const onHandleDialogCancel = () => {
  emits('onDialogCancel');
}

const onHandleDialogError = (error: any) => {
  emits('onFetchError', { error });
}

const onHandleDialogConfirm = async () => {
  if(!props.user) return;

  const { valid } = await form.value?.validate();
  if (!valid) return;

  // send empty values as undefined. Empty strings will throw validation errors
  // which means you will not be able to remove optional values, but thats an issue with the Auth0 sdk
  if(!updateRequest.value.email) updateRequest.value.email = undefined;
  if(!updateRequest.value.firstName) updateRequest.value.firstName = undefined;
  if(!updateRequest.value.lastName) updateRequest.value.lastName = undefined;

  const { error } = await updateIdentityUser(props.user.userId, updateRequest.value);

  if(error){
    onHandleDialogError( { error });
    return
  }

  emits('onDialogConfirm')
}

onMounted( async () => {
  isDialogVisible.value = true;
  await nextTick();
  const { valid } = await form.value?.validate();
  isFormValid.value = !!(typeof valid === 'boolean' ? valid : valid?.valid);
});

onUnmounted(() => {
  isDialogVisible.value = false;
})

</script>

